import type { SearchTerminalContext } from "@wags/discovery/index.ts";
import type { CaptureResult } from "posthog-js";
import { z } from "zod";
import {
    deferredPostHog,
    runWhenPostHogReady,
} from "@/lib/core/deferred-posthog-client";
import {
    searchCallerSurfaceSchema,
    searchSubmissionSourceSchema,
    searchTerminalActionSchema,
    searchTerminalContextSchema,
} from "@wags/discovery/search/search-correlation.contract.ts";

export const SEARCH_SUBMITTED_POSTHOG_EVENT =
    "discovery.search.submitted" as const;

export type SearchSubmissionPostHogProperties = Pick<
    SearchTerminalContext,
    | "searchInteractionId"
    | "submissionId"
    | "queryRevision"
    | "submissionSource"
    | "action"
    | "surface"
> & { $insert_id: string };

type CaptureSearchSubmission = (
    eventName: typeof SEARCH_SUBMITTED_POSTHOG_EVENT,
    properties: SearchSubmissionPostHogProperties,
    options: { timestamp: Date },
) => CaptureResult | null | void;

const searchSubmissionPostHogPropertiesSchema = z
    .object({
        searchInteractionId: z.uuid(),
        submissionId: z.uuid(),
        queryRevision: z.number().int().nonnegative(),
        submissionSource: searchSubmissionSourceSchema,
        action: searchTerminalActionSchema,
        surface: searchCallerSurfaceSchema,
        $insert_id: z.uuid(),
    })
    .refine(({ submissionId, $insert_id }) => $insert_id === submissionId);

const postHogSessionIdSchema = z.uuid();
const postHogWindowIdSchema = z.uuid();

function safePostHogSessionProperties(properties: CaptureResult["properties"]) {
    const sessionId = postHogSessionIdSchema.safeParse(properties.$session_id);
    if (!sessionId.success) return {};

    const windowId = postHogWindowIdSchema.safeParse(properties.$window_id);
    return {
        $session_id: sessionId.data,
        ...(windowId.success ? { $window_id: windowId.data } : {}),
    };
}

function safePostHogScopeProperties(properties: CaptureResult["properties"]) {
    const hostValue = properties.$host;
    let host: string | undefined;
    if (typeof hostValue === "string") {
        const [hostname, port, extra] = hostValue.toLowerCase().split(":");
        const labels = hostname.split(".");
        const validHostname =
            labels.length > 0 &&
            labels.every((label) =>
                /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label),
            );
        const validPort =
            port === undefined ||
            (/^\d{1,5}$/.test(port) && Number(port) <= 65_535);
        if (validHostname && validPort && extra === undefined) {
            host = hostValue.toLowerCase();
        }
    }
    const environment =
        typeof properties.environment === "string" &&
        /^[a-z0-9_-]{1,32}$/i.test(properties.environment)
            ? properties.environment.toLowerCase()
            : undefined;

    return {
        ...(host ? { $host: host } : {}),
        ...(environment ? { environment } : {}),
    };
}

export function scrubSearchSubmittedCapture(
    capture: CaptureResult | null,
): CaptureResult | null {
    if (!capture || capture.event !== SEARCH_SUBMITTED_POSTHOG_EVENT) {
        return capture;
    }

    const parsedProperties = searchSubmissionPostHogPropertiesSchema.safeParse(
        capture.properties,
    );
    if (!parsedProperties.success) return null;

    const properties = {
        ...(typeof capture.properties.token === "string"
            ? { token: capture.properties.token }
            : {}),
        ...(typeof capture.properties.distinct_id === "string" ||
        typeof capture.properties.distinct_id === "number"
            ? { distinct_id: capture.properties.distinct_id }
            : {}),
        ...safePostHogSessionProperties(capture.properties),
        ...safePostHogScopeProperties(capture.properties),
        ...parsedProperties.data,
    };

    return {
        uuid: capture.uuid,
        event: capture.event,
        ...(capture.timestamp ? { timestamp: capture.timestamp } : {}),
        properties,
    };
}

export function commitSearchSubmission(
    draft: Omit<SearchTerminalContext, "submissionId">,
    capture?: CaptureSearchSubmission,
): SearchTerminalContext {
    const timestamp = new Date();
    const context = searchTerminalContextSchema.parse({
        ...draft,
        submissionId: crypto.randomUUID(),
    });
    captureSearchSubmission(context, capture, timestamp);
    return context;
}

export function captureSearchSubmission(
    context: SearchTerminalContext,
    capture?: CaptureSearchSubmission,
    timestamp = new Date(),
): void {
    const parsedContext = searchTerminalContextSchema.safeParse(context);
    if (!parsedContext.success) return;

    const {
        searchInteractionId,
        submissionId,
        queryRevision,
        submissionSource,
        action,
        surface,
    } = parsedContext.data;
    const dispatch = () => {
        try {
            (capture ?? deferredPostHog.capture)(
                SEARCH_SUBMITTED_POSTHOG_EVENT,
                {
                    searchInteractionId,
                    submissionId,
                    queryRevision,
                    submissionSource,
                    action,
                    surface,
                    $insert_id: submissionId,
                },
                { timestamp },
            );
        } catch {}
    };

    try {
        if (capture) dispatch();
        else runWhenPostHogReady(deferredPostHog, dispatch);
    } catch {}
}
